import { useCallback, useRef, useState } from 'react';
import ModalAvisoContactoSoft from '../components/contacto/ModalAvisoContactoSoft';
import { buildAvisoContactoSoft, debeRevisarDocumentoEnInput } from '../utils/contactoSoftValidation';

/**
 * Avisos suaves por contacto (apellidos ES, documento, teléfono).
 * @param {string} scopeId — identificador único (titular, prop:0, cliente-uuid…)
 */
export function useContactoSoftWarnings(scopeId = 'default') {
  const dismissedRef = useRef(new Set());
  const [pending, setPending] = useState(null);

  const dismissKeyFull = useCallback((key) => {
    dismissedRef.current.add(`${scopeId}:${key}`);
  }, [scopeId]);

  const isDismissed = useCallback((key) => {
    return dismissedRef.current.has(`${scopeId}:${key}`);
  }, [scopeId]);

  const showAviso = useCallback((aviso, value, ctx) => {
    if (!aviso || isDismissed(aviso.dismissKey)) return Promise.resolve(value);
    return new Promise((resolve) => {
      setPending({
        aviso,
        value,
        ctx,
        resolve,
      });
    });
  }, [isDismissed]);

  const checkField = useCallback(async (tipo, value, ctx = {}) => {
    const aviso = buildAvisoContactoSoft(tipo, value, ctx);
    return showAviso(aviso, value, ctx);
  }, [showAviso]);

  const handleOmitir = useCallback((nuevoValor) => {
    if (!pending) return;
    dismissKeyFull(pending.aviso.dismissKey);
    pending.resolve(nuevoValor != null ? nuevoValor : pending.value);
    setPending(null);
  }, [pending, dismissKeyFull]);

  const bindApellidos = useCallback((ctx, value, onChange) => ({
    onBlur: () => {
      const aviso = buildAvisoContactoSoft('apellidos', value, ctx);
      if (!aviso) return;
      showAviso(aviso, value, ctx).then((v) => {
        if (v !== value) onChange(v);
      });
    },
  }), [showAviso]);

  const bindDocumento = useCallback((ctx, value, onChange) => ({
    onBlur: () => {
      const aviso = buildAvisoContactoSoft('dni', value, ctx);
      if (!aviso) return;
      showAviso(aviso, value, ctx).then((v) => {
        if (v !== value) onChange(v);
      });
    },
    onChange: (e) => {
      const v = e.target.value;
      onChange(v);
      if (debeRevisarDocumentoEnInput(v)) {
        const aviso = buildAvisoContactoSoft('dni', v, ctx);
        if (!aviso) return;
        showAviso(aviso, v, ctx).then((nv) => {
          if (nv !== v) onChange(nv);
        });
      }
    },
  }), [showAviso]);

  const bindTelefono = useCallback((ctx, value, onChange) => ({
    onBlur: () => {
      const aviso = buildAvisoContactoSoft('telefono', value, ctx);
      if (!aviso) return;
      showAviso(aviso, value, ctx).then((v) => {
        if (v !== value) onChange(v);
      });
    },
  }), [showAviso]);

  const bindEmail = useCallback((ctx, value, onChange) => ({
    onBlur: () => {
      const aviso = buildAvisoContactoSoft('email', value, ctx);
      if (!aviso) return;
      showAviso(aviso, value, ctx).then((v) => {
        if (v !== value) onChange(v);
      });
    },
  }), [showAviso]);

  const modal = (
    <ModalAvisoContactoSoft
      open={!!pending}
      mensaje={pending?.aviso?.mensaje}
      fieldLabel={pending?.aviso?.fieldLabel}
      value={pending?.value ?? ''}
      inputType={pending?.aviso?.inputType || 'text'}
      onOmitir={handleOmitir}
    />
  );

  return {
    modal,
    checkField,
    bindApellidos,
    bindDocumento,
    bindTelefono,
    bindEmail,
  };
}
